import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Button,
  Alert,
  Snackbar,
  IconButton,
  Grid,
  Autocomplete,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { useTenant } from "../contexts/TenantContext";
import residentService from "../services/residentService";
import OrderMedicationService from "../services/OrderMedicationService";
import { ResidentDTO } from "../types/resident.types";
import { MedicationOrderDTO, MedicationItemDTO } from "../types/medication.types";

interface StockRow {
  rowId: string;
  selectedMedicationItemId: string;
  addedStock: string;
}

const StockAvailabilityPage: React.FC = () => {
  const { tenant, currentTenantId } = useTenant();
  const facilityId = currentTenantId || tenant?.id;
  const [residents, setResidents] = useState<ResidentDTO[]>([]);
  const [selectedResidentId, setSelectedResidentId] = useState<string>("");
  const [loadingResidents, setLoadingResidents] = useState<boolean>(false);

  const [medicationOrders, setMedicationOrders] = useState<MedicationOrderDTO[]>([]);
  const [medicationStock, setMedicationStock] = useState<any[]>([]);
  const [loadingMedications, setLoadingMedications] = useState<boolean>(false);

  const [rows, setRows] = useState<StockRow[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (facilityId) {
      fetchResidents(facilityId);
    }
  }, [facilityId]);

  useEffect(() => {
    if (selectedResidentId) {
      fetchMedications(selectedResidentId);
      // Reset rows when resident changes
      setRows([{ rowId: Date.now().toString(), selectedMedicationItemId: "", addedStock: "" }]);
    } else {
      setMedicationOrders([]);
      setMedicationStock([]);
      setRows([]);
    }
  }, [selectedResidentId]);

  const fetchResidents = async (facilityId: string) => {
    setLoadingResidents(true);
    try {
      const data = await residentService.getActiveLookup(facilityId);
      setResidents(data || []);
    } catch (error) {
      console.error("Failed to fetch active residents", error);
    } finally {
      setLoadingResidents(false);
    }
  };

  const fetchMedications = async (residentId: string) => {
    setLoadingMedications(true);
    try {
      const [orders, stock] = await Promise.all([
        OrderMedicationService.getActiveOrdersByResident(residentId),
        OrderMedicationService.getMedicationStockByResident(residentId)
      ]);
      console.log("Active Orders fetched:", orders);
      console.log("Medication Stock GET API response:", stock);
      setMedicationOrders(orders || []);
      
      // Ensure medicationStock is always an array to prevent UI crashes if backend returns an object
      setMedicationStock(Array.isArray(stock) ? stock : (stock ? [stock] : []));
    } catch (error) {
      console.error("Failed to fetch medications and stock", error);
    } finally {
      setLoadingMedications(false);
    }
  };

  const allItems: Array<{ id: string; medicationName: string; dosage: string; frequency: string; currentStock: any }> = [];
  
  // Only use medications from the stock API
  if (Array.isArray(medicationStock)) {
    medicationStock.forEach((stockItem: any) => {
      const medId = stockItem.medicationId || stockItem.id || stockItem.medicationItemId;
      if (medId) {
        allItems.push({
          id: medId,
          medicationName: stockItem.medicationName || "Unknown",
          dosage: stockItem.dose || stockItem.dosage || "",
          frequency: stockItem.schedule || stockItem.frequency || "",
          currentStock: stockItem.currentStock ?? stockItem.quantity ?? stockItem.stockQuantity ?? stockItem.stock ?? stockItem.availableStock ?? 0,
        });
      }
    });
  }

  const handleAddRow = () => {
    setRows([...rows, { rowId: Date.now().toString(), selectedMedicationItemId: "", addedStock: "" }]);
  };

  const handleRemoveRow = (rowId: string) => {
    setRows(rows.filter((r) => r.rowId !== rowId));
  };

  const handleRowChange = (rowId: string, field: keyof StockRow, value: string) => {
    setRows(rows.map((r) => (r.rowId === rowId ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const adjustments: { medicationId: string; quantity: number }[] = [];
      
      rows.forEach((r) => {
        if (r.selectedMedicationItemId && r.addedStock) {
          const numStock = parseInt(r.addedStock, 10);
          if (!isNaN(numStock) && numStock > 0) {
            adjustments.push({
                medicationId: r.selectedMedicationItemId,
                quantity: numStock
            });
          }
        }
      });

      if (adjustments.length === 0) {
        setSnackbar({ open: true, message: "No valid stock to update.", severity: "error" });
        setSaving(false);
        return;
      }

      await OrderMedicationService.bulkAddStock({
          residentId: selectedResidentId,
          adjustments: adjustments
      });

      setSnackbar({
        open: true,
        message: "Stock updated successfully!",
        severity: "success",
      });

      // Reset the screen immediately as requested
      setSelectedResidentId("");
    } catch (error) {
      console.error("Failed to update stock", error);
      setSnackbar({
        open: true,
        message: "Failed to update stock. Please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, margin: "0 auto" }}>
      <Typography variant="h5" gutterBottom sx={{ color: "#1A2B45", fontWeight: 700, mb: 3 }}>
        Stock Availability
      </Typography>

      <Paper sx={{ p: 3, mb: 4, borderRadius: 2, border: "1px solid #E2E8F0", boxShadow: "none" }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete
              options={residents.filter(r => r?.status?.toUpperCase() === "ACTIVE").sort((a: any, b: any) => {
                const nameA = (a.residentName || `${a.firstName || ""} ${a.lastName || ""}`).toLowerCase().trim();
                const nameB = (b.residentName || `${b.firstName || ""} ${b.lastName || ""}`).toLowerCase().trim();
                return nameA.localeCompare(nameB);
              })}
              value={residents.find(r => r.id === selectedResidentId) || null}
              getOptionLabel={(option) => `${(option as any).residentName || `${option.firstName} ${option.lastName}`} ${option.roomNumber ? `(Room: ${option.roomNumber}${option.bedNumber ? `, Bed: ${option.bedNumber}` : ""})` : ""}`}
              onChange={(event, newValue) => {
                setSelectedResidentId(newValue ? newValue.id || "" : "");
              }}
              disabled={loadingResidents}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Resident by Name"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingResidents ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {selectedResidentId && (
        <Paper sx={{ p: 4, borderRadius: 2, border: "1px solid #E2E8F0", boxShadow: "none" }}>

          {loadingMedications ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : allItems.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
              No active medications found for this resident.
            </Typography>
          ) : (
            <>
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ color: "#5C6F85", fontWeight: 600, borderBottom: "1px solid #E2E8F0", pb: 1, mb: 3 }}>
                  Update Medication Stock
                </Typography>

                {/* Table Header like styling */}
                <Grid container spacing={2} sx={{ mb: 1, px: 1, display: { xs: 'none', md: 'flex' } }}>
                  <Grid item md={4}><Typography variant="caption" sx={{ fontWeight: 700, color: "#5C6F85" }}>MEDICATION NAME</Typography></Grid>
                  <Grid item md={2}><Typography variant="caption" sx={{ fontWeight: 700, color: "#5C6F85" }}>DOSAGE</Typography></Grid>
                  <Grid item md={2}><Typography variant="caption" sx={{ fontWeight: 700, color: "#5C6F85" }}>FREQUENCY</Typography></Grid>
                  <Grid item md={2}><Typography variant="caption" sx={{ fontWeight: 700, color: "#5C6F85" }}>CURRENT STOCK</Typography></Grid>
                  <Grid item md={2}><Typography variant="caption" sx={{ fontWeight: 700, color: "#5C6F85" }}>ADD STOCK QTY</Typography></Grid>
                </Grid>

                {/* Dynamic Rows */}
                {rows.map((row, index) => {
                  const selectedItem = allItems.find(i => i.id === row.selectedMedicationItemId);
                  
                  let currentStockDisplay: any = "N/A";
                  if (selectedItem) {
                    currentStockDisplay = selectedItem.currentStock;
                  }

                  const selectedMedIdsForFilter = rows.map((r) => r.selectedMedicationItemId).filter(Boolean);

                  return (
                    <Box
                      key={row.rowId}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        border: "1px solid #E2E8F0",
                        backgroundColor: "#F8FAFC",
                        position: "relative",
                        pr: { xs: 5, md: 2 }
                      }}
                    >
                      {rows.length > 1 && (
                        <IconButton
                          onClick={() => handleRemoveRow(row.rowId)}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            color: "#64748B",
                            "&:hover": { color: "#EF4444" }
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth size="small">
                            {!row.selectedMedicationItemId && <InputLabel sx={{ background: '#F8FAFC', px: 0.5 }}>Select Medication</InputLabel>}
                            <Select
                              value={row.selectedMedicationItemId}
                              onChange={(e) => handleRowChange(row.rowId, "selectedMedicationItemId", e.target.value)}
                              displayEmpty
                              sx={{ bgcolor: "white" }}
                            >
                              {allItems
                                .filter((item) => item.id === row.selectedMedicationItemId || (item.id && !selectedMedIdsForFilter.includes(item.id)))
                                .map((item) => (
                                  <MenuItem key={item.id} value={item.id}>
                                    {item.medicationName}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Dosage"
                            value={selectedItem?.dosage || ""}
                            disabled
                            sx={{ bgcolor: "white", "& .Mui-disabled": { WebkitTextFillColor: "#1A2B45" } }}
                          />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Frequency"
                            value={selectedItem?.frequency || ""}
                            disabled
                            sx={{ bgcolor: "white", "& .Mui-disabled": { WebkitTextFillColor: "#1A2B45" } }}
                          />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Current Stock"
                            value={currentStockDisplay}
                            disabled
                            sx={{ bgcolor: "#E2E8F0", "& .Mui-disabled": { WebkitTextFillColor: "#1A2B45", fontWeight: "bold" } }}
                          />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="Enter Qty"
                            inputProps={{ min: 0 }}
                            value={row.addedStock}
                            onChange={(e) => handleRowChange(row.rowId, "addedStock", e.target.value)}
                            sx={{ bgcolor: "white" }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}

                {(() => {
                  const currentSelectedIds = rows.map(r => r.selectedMedicationItemId).filter(Boolean);
                  const hasAvailableMeds = allItems.some((item) => item.id && !currentSelectedIds.includes(item.id));

                  return hasAvailableMeds ? (
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={handleAddRow}
                      sx={{ color: "#0077B6", fontWeight: 600, mt: 1 }}
                    >
                      Add Medication
                    </Button>
                  ) : null;
                })()}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid #E2E8F0", pt: 3 }}>
                <Button
                  variant="outlined"
                  sx={{ mr: 2, color: "#5C6F85", borderColor: "#CBD5E1" }}
                  onClick={() => {
                    setRows([{ rowId: Date.now().toString(), selectedMedicationItemId: "", addedStock: "" }]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !rows.some(r => r.selectedMedicationItemId && r.addedStock)}
                  sx={{
                    bgcolor: "#48CAE4",
                    color: "#000000",
                    fontWeight: "normal",
                    "&:hover": { bgcolor: "#3ab3cc" },
                    "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.12)", color: "rgba(0,0,0,0.26)" },
                    px: 4,
                  }}
                >
                  {saving ? "Saving..." : "Submit Order"}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StockAvailabilityPage;
